import {
    Alert,
    AlertDescription,
    AlertIcon,
    Button,
    FormControl,
    FormLabel,
    Input,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Textarea,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import { fetchAllRoutes, fetchLabLocationsOutside, getAllFacilitiesOutside, updateRoutePickup } from '../../api';
import { getSelectStyles, useTableColors } from '../../Utils';

const dayOptions = [
    { value: 'Monday', label: 'Monday' },
    { value: 'Tuesday', label: 'Tuesday' },
    { value: 'Wednesday', label: 'Wednesday' },
    { value: 'Thursday', label: 'Thursday' },
    { value: 'Friday', label: 'Friday' },
    { value: 'Saturday', label: 'Saturday' },
    { value: 'Sunday', label: 'Sunday' }
];

const EditPickupModal = ({ isOpen, onClose, pickupData, onPickupUpdated }) => {
    const [pickupType, setPickupType] = useState('');
    const [pickupLocation, setPickupLocation] = useState('');
    const [dropOffLocation, setDropOffLocation] = useState('');
    const [route, setRoute] = useState('');
    const [days, setDays] = useState([]);
    const [pickupOrder, setPickupOrder] = useState(1);
    const [note, setNote] = useState('');
    const [facilities, setFacilities] = useState([]);
    const [labLocations, setLabLocations] = useState([]);
    const [routes, setRoutes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [errorModal, setErrorModal] = useState(null);
    const coolors = useTableColors();


    useEffect(() => {
        if (pickupData) {
            setPickupType(pickupData?.type);
            setPickupLocation(pickupData?.pickupLocation?._id || '');
            setDropOffLocation(pickupData?.dropOffLocation?._id || '');
            setRoute(pickupData?.routeId?._id || '');
            setDays(pickupData?.pickupDays.map(day => ({ value: day.day, label: day.day })));
            setPickupOrder(pickupData?.pickupDays[0]?.pickupOrder || 1);
            setNote(pickupData?.note || '');
        }
    }, [pickupData]);

    useEffect(() => {
        getFacilities();
        getRoutes();
        getLabLocations();
    }, []);

    const getFacilities = async () => {
        try {
            const response = await getAllFacilitiesOutside();
            if (response.success) {
                setFacilities(response.data.facilities);
            }
        } catch (error) {
            setErrorModal('Error loading facilities');
        }
    };

    const getLabLocations = async () => {
        try {
            const response = await fetchLabLocationsOutside();
            if (response.success) {
                setLabLocations(response.data);
            }
        } catch (error) {
            setErrorModal('Error loading lab locations');
        }
    };

    const getRoutes = async () => {
        try {
            const response = await fetchAllRoutes(1, 999999);
            if (response.success) {
                setRoutes(response.data.routes);
            }
        } catch (error) {
            setErrorModal('Error loading routes');
        }
    };

    const handleUpdatePickup = async () => {
        setErrorModal(null);

        if (!route || days.length === 0) {
            setErrorModal('Please fill in all required fields.');
            return;
        }
        setLoading(true);
        try {
            const response = await updateRoutePickup(pickupData._id, {
                type: pickupType,
                pickupLocation,
                dropOffLocation,
                routeId: route,
                pickupDays: days.map(day => ({ day: day.value, pickupOrder })),
                note
            });

            if (response.success) {

                onPickupUpdated(response.data);
                onClose();
            } else {
                setErrorModal(response.error);
            }
        } catch (error) {
            setErrorModal('Error updating pickup');
        }
        setLoading(false);
    };



    return (
        <Modal size={'3xl'} isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Edit Pickup</ModalHeader>
                <ModalBody>
                    {errorModal && <Alert status="error"><AlertIcon /><AlertDescription>{errorModal}</AlertDescription></Alert>}

                    <FormControl mb={3}>
                        <FormLabel>Pickup Type</FormLabel>
                        <Select
                            styles={getSelectStyles(coolors)}
                            classNamePrefix="rs"
                            options={[
                                { value: 'Default', label: 'Default' },
                                { value: 'Supplies', label: 'Supplies' },
                                { value: 'DropOffOnly', label: 'Drop-Off Only' }
                            ]}
                            value={{ value: pickupType, label: pickupType }}
                            onChange={(option) => setPickupType(option.value)}
                        />
                    </FormControl>

                    <FormControl mb={3} isRequired>
                        <FormLabel>Pickup Location</FormLabel>
                        <Select
                            styles={getSelectStyles(coolors)}
                            classNamePrefix="rs"
                            options={facilities.map(fac => ({
                                value: fac._id,
                                label: `${fac.name} - ${fac.address.city}`
                            }))}
                            value={pickupLocation ? { value: pickupLocation, label: facilities.find(f => f._id === pickupLocation)?.name } : null}
                            onChange={(option) => setPickupLocation(option ? option.value : '')}
                            placeholder="Select a facility"
                            isClearable
                        />
                    </FormControl>

                    <FormControl mb={3} isRequired>
                        <FormLabel>Drop Off Location</FormLabel>
                        <Select
                            styles={getSelectStyles(coolors)}
                            classNamePrefix="rs"
                            options={labLocations.map(lab => ({ value: lab._id, label: lab.name }))}
                            value={dropOffLocation ? { value: dropOffLocation, label: labLocations.find(l => l._id === dropOffLocation)?.name } : null}
                            onChange={(option) => setDropOffLocation(option ? option.value : '')}
                        />
                    </FormControl>

                    <FormControl mb={3} isRequired>
                        <FormLabel>Route</FormLabel>
                        <Select
                            styles={getSelectStyles(coolors)}
                            classNamePrefix="rs"
                            options={routes.map(route => ({ value: route._id, label: route.name }))}
                            value={route ? { value: route, label: routes.find(r => r._id === route)?.name } : null}
                            onChange={(option) => setRoute(option ? option.value : '')}
                        />
                    </FormControl>

                    <FormControl mb={3} isRequired>
                        <FormLabel>Days</FormLabel>
                        <Select
                            styles={getSelectStyles(coolors)}
                            classNamePrefix="rs"
                            isMulti
                            options={dayOptions}
                            value={days}
                            onChange={(selectedOptions) => setDays(selectedOptions || [])}
                            closeMenuOnSelect={false}
                        />
                    </FormControl>

                    <FormControl style={{ display: 'none' }} mb={3}>
                        <FormLabel>Pickup Order</FormLabel>
                        <Input
                            type="number"
                            value={pickupOrder}
                            onChange={(e) => setPickupOrder(Number(e.target.value))}
                        />
                    </FormControl>

                    <FormControl mb={3}>
                        <FormLabel>Note</FormLabel>
                        <Textarea
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                        />
                    </FormControl>
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} mr={3}>Close</Button>
                    <Button isLoading={loading} colorScheme="blue" onClick={handleUpdatePickup}>Update</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default EditPickupModal;
